import { Static, Type } from '@sinclair/typebox';
import { AiFieldKind } from '../ai-assist';
import { CroppableImageResponseSchema, CroppableImageSchema } from '../image';
import {
  ProResourceBookingMode,
  ProResourceCategory,
  ProResourceType,
} from '../translation_namespace/pro_resource_namespace';
import { AvailabilityBookabilityImpactSchema } from '../availability-bookability';
import { SortConfig, SortDirection } from '../filter/sort';

export const ProResourceCategoryEnum = Type.Union(
  [
    Type.Literal(ProResourceCategory.VEHICLE),
    Type.Literal(ProResourceCategory.ROOM),
    Type.Literal(ProResourceCategory.EQUIPMENT),
  ],
  {
    title: 'INPUT_PRO_RESOURCE_CATEGORY_TITLE',
    placeholder: 'INPUT_PRO_RESOURCE_CATEGORY_PLACEHOLDER',
  },
);

export const ProResourceBookingModeEnum = Type.Union(
  [
    Type.Literal(ProResourceBookingMode.CONSTRAINT),
    Type.Literal(ProResourceBookingMode.SELECTABLE),
    Type.Literal(ProResourceBookingMode.POOL),
  ],
  {
    title: 'INPUT_PRO_RESOURCE_BOOKING_MODE_TITLE',
    placeholder: 'INPUT_PRO_RESOURCE_BOOKING_MODE_PLACEHOLDER',
  },
);

export const ProResourceTypeEnum = Type.Union(
  [Type.Literal(ProResourceType.REUSABLE), Type.Literal(ProResourceType.CONSUMABLE)],
  {
    title: 'INPUT_PRO_RESOURCE_TYPE_TITLE',
    placeholder: 'INPUT_PRO_RESOURCE_TYPE_PLACEHOLDER',
  },
);

const NameSchema = Type.String({
  maxLength: 255,
  minLength: 1,
  title: 'INPUT_PRO_RESOURCE_NAME_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_NAME_PLACEHOLDER',
  meta: { ai: { kind: AiFieldKind.SERVICE_NAME } },
});

// Placeholder dédié : les exemples génériques (« Cabine n°1, Tapis de yoga… ») n'ont
// aucun sens pour une fiche véhicule.
const VehicleNameSchema = Type.String({
  maxLength: 255,
  minLength: 1,
  title: 'INPUT_PRO_RESOURCE_NAME_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_VEHICLE_NAME_PLACEHOLDER',
  meta: { ai: { kind: AiFieldKind.SERVICE_NAME } },
});

const DescriptionSchema = Type.String({
  title: 'INPUT_PRO_RESOURCE_DESCRIPTION_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_DESCRIPTION_PLACEHOLDER',
  meta: { multiline: true, ai: { kind: AiFieldKind.SHORT_DESCRIPTION } },
});

const QuantitySchema = Type.Integer({
  minimum: 1,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_QUANTITY_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_QUANTITY_PLACEHOLDER',
});

/**
 * Jauge d'UNE unité, en PERSONNES. Le parallélisme est porté par `quantity` (ressource
 * exclusive) ou par `quantity × capacity` (ressource partagée) — cf. `resourcePlacesBudget`.
 */
const CapacitySchema = Type.Integer({
  minimum: 1,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_CAPACITY_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_CAPACITY_PLACEHOLDER',
});

const IsExclusiveSchema = Type.Boolean({
  title: 'INPUT_PRO_RESOURCE_IS_EXCLUSIVE_TITLE',
});

const IsActiveSchema = Type.Boolean({
  title: 'INPUT_PRO_RESOURCE_IS_ACTIVE_TITLE',
});

const SortOrderSchema = Type.Integer({ minimum: 0 });

// Taille de flotte d'une fiche véhicule (une fiche = une CLASSE de véhicules interchangeables,
// pas une voiture physique — l'identité à l'unité, plaque/modèle, a été abandonnée).
const VehicleQuantitySchema = Type.Integer({
  minimum: 1,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_VEHICLE_QUANTITY_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_VEHICLE_QUANTITY_PLACEHOLDER',
});

// Même pattern que pack/prestation : le formulaire soumet { url: clé S3 brute, crop? }[],
// le backend stocke tel quel (JSONB) et renvoie des CroppableImageResponse signés.
const PicturesSchema = Type.Array(CroppableImageSchema, {
  minItems: 0,
  maxItems: 5,
  meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
  title: 'INPUT_PRO_RESOURCE_PICTURES_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_PICTURES_PLACEHOLDER',
});

const PAX_MIN_OPTIONS = {
  minimum: 0,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_PAX_MIN_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_PAX_MIN_PLACEHOLDER',
};
const PAX_MAX_OPTIONS = {
  minimum: 1,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_PAX_MAX_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_PAX_MAX_PLACEHOLDER',
};

const PaxMinSchema = Type.Integer(PAX_MIN_OPTIONS);
const PaxMaxSchema = Type.Integer(PAX_MAX_OPTIONS);

const SmallLuggageMinOptions = {
  minimum: 0,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_SMALL_LUGGAGE_MIN_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_SMALL_LUGGAGE_MIN_PLACEHOLDER',
};
const SmallLuggageMinSchema = Type.Integer(SmallLuggageMinOptions);
const NullableSmallLuggageMinSchema = Type.Union([Type.Integer(SmallLuggageMinOptions), Type.Null()], {
  ...SmallLuggageMinOptions,
  meta: { keep_null: true },
});

const SmallLuggageMaxOptions = {
  minimum: 0,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_SMALL_LUGGAGE_MAX_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_SMALL_LUGGAGE_MAX_PLACEHOLDER',
};
const SmallLuggageMaxSchema = Type.Integer(SmallLuggageMaxOptions);
const NullableSmallLuggageMaxSchema = Type.Union([Type.Integer(SmallLuggageMaxOptions), Type.Null()], {
  ...SmallLuggageMaxOptions,
  meta: { keep_null: true },
});

const LargeLuggageMinOptions = {
  minimum: 0,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_LARGE_LUGGAGE_MIN_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_LARGE_LUGGAGE_MIN_PLACEHOLDER',
};
const LargeLuggageMinSchema = Type.Integer(LargeLuggageMinOptions);
const NullableLargeLuggageMinSchema = Type.Union([Type.Integer(LargeLuggageMinOptions), Type.Null()], {
  ...LargeLuggageMinOptions,
  meta: { keep_null: true },
});

const LargeLuggageMaxOptions = {
  minimum: 0,
  maximum: 10_000,
  title: 'INPUT_PRO_RESOURCE_LARGE_LUGGAGE_MAX_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_LARGE_LUGGAGE_MAX_PLACEHOLDER',
};
const LargeLuggageMaxSchema = Type.Integer(LargeLuggageMaxOptions);
const NullableLargeLuggageMaxSchema = Type.Union([Type.Integer(LargeLuggageMaxOptions), Type.Null()], {
  ...LargeLuggageMaxOptions,
  meta: { keep_null: true },
});

/**
 * Bornes préremplies à la CRÉATION d'un véhicule : des champs vides (= aucune limite)
 * déroutaient les pros, un exemple explicite (1-99 passagers, 0-99 petits et grands bagages) évite la question.
 * Le pro peut toujours vider un champ pour retirer la limite.
 */
export const VEHICLE_DEFAULT_PAX_MIN = 1;
export const VEHICLE_DEFAULT_LUGGAGE_MIN = 0;
export const VEHICLE_DEFAULT_CAPACITY_MAX = 99;

const PrestationIdsSchema = Type.Array(Type.Integer(), {
  maxItems: 500,
  title: 'INPUT_PRO_RESOURCE_PRESTATION_IDS_TITLE',
  meta: { input_type: 'prestation_selector' },
});

const ProPlaceIdsSchema = Type.Array(Type.Integer(), {
  maxItems: 500,
  title: 'INPUT_PRO_RESOURCE_PLACE_IDS_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_PLACE_IDS_PLACEHOLDER',
  meta: { input_type: 'pro_place_selector' },
});

// Form-only schema: prestation_selector forces string values in the form state.
// Backend continues to expect Type.Integer arrays.
const PrestationIdsFormSchema = Type.Array(
  Type.String({
    meta: { input_type: 'prestation_selector' },
  }),
  {
    title: 'INPUT_PRO_RESOURCE_PRESTATION_IDS_TITLE',
    placeholder: 'INPUT_PRO_RESOURCE_PRESTATION_IDS_PLACEHOLDER',
    meta: { input_type: 'prestation_selector' },
  },
);

const ProPlaceIdsFormSchema = Type.Array(Type.Integer(), {
  maxItems: 500,
  title: 'INPUT_PRO_RESOURCE_PLACE_IDS_TITLE',
  placeholder: 'INPUT_PRO_RESOURCE_PLACE_IDS_PLACEHOLDER',
  meta: { input_type: 'pro_place_selector' },
});

export const CreateProResourceRequestSchema = Type.Object({
  name: NameSchema,
  description: Type.Optional(DescriptionSchema),
  category: ProResourceCategoryEnum,
  type: Type.Optional(ProResourceTypeEnum),
  booking_mode: Type.Optional(ProResourceBookingModeEnum),
  quantity: Type.Optional(QuantitySchema),
  capacity: Type.Optional(CapacitySchema),
  is_exclusive: Type.Optional(IsExclusiveSchema),
  is_active: Type.Optional(IsActiveSchema),
  sort_order: Type.Optional(SortOrderSchema),
  pax_min: Type.Optional(PaxMinSchema),
  pax_max: Type.Optional(PaxMaxSchema),
  small_luggage_min: Type.Optional(SmallLuggageMinSchema),
  small_luggage_max: Type.Optional(SmallLuggageMaxSchema),
  large_luggage_min: Type.Optional(LargeLuggageMinSchema),
  large_luggage_max: Type.Optional(LargeLuggageMaxSchema),
  pictures: Type.Optional(PicturesSchema),
  prestation_ids: Type.Optional(PrestationIdsSchema),
  pro_place_ids: Type.Optional(ProPlaceIdsSchema),
});
export type CreateProResourceRequestObject = Static<typeof CreateProResourceRequestSchema>;

export const UpdateProResourceRequestSchema = Type.Object({
  name: Type.Optional(NameSchema),
  description: Type.Optional(DescriptionSchema),
  category: Type.Optional(ProResourceCategoryEnum),
  type: Type.Optional(ProResourceTypeEnum),
  booking_mode: Type.Optional(ProResourceBookingModeEnum),
  quantity: Type.Optional(QuantitySchema),
  capacity: Type.Optional(CapacitySchema),
  is_exclusive: Type.Optional(IsExclusiveSchema),
  is_active: Type.Optional(IsActiveSchema),
  sort_order: Type.Optional(SortOrderSchema),
  pax_min: Type.Optional(PaxMinSchema),
  pax_max: Type.Optional(PaxMaxSchema),
  small_luggage_min: Type.Optional(NullableSmallLuggageMinSchema),
  small_luggage_max: Type.Optional(NullableSmallLuggageMaxSchema),
  large_luggage_min: Type.Optional(NullableLargeLuggageMinSchema),
  large_luggage_max: Type.Optional(NullableLargeLuggageMaxSchema),
  pictures: Type.Optional(PicturesSchema),
  prestation_ids: Type.Optional(PrestationIdsSchema),
  pro_place_ids: Type.Optional(ProPlaceIdsSchema),
});
export type UpdateProResourceRequestObject = Static<typeof UpdateProResourceRequestSchema>;

// `ordered_ids` peut n'être qu'une partie des ressources du pro (ex. la page Matériel ne liste
// que la catégorie EQUIPMENT) : les ids envoyés sont permutés entre eux, les autres gardent leur place.
export const ReorderProResourcesRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
});
export type ReorderProResourcesRequestObject = Static<typeof ReorderProResourcesRequestSchema>;

export const ProResourceResponseSchema = Type.Object({
  pro_resource_id: Type.Number(),
  professional_id: Type.Number(),
  name: Type.String(),
  description: Type.Optional(Type.String()),
  category: ProResourceCategoryEnum,
  type: ProResourceTypeEnum,
  booking_mode: ProResourceBookingModeEnum,
  quantity: Type.Number(),
  capacity: Type.Number(),
  is_exclusive: Type.Boolean(),
  is_active: Type.Boolean(),
  sort_order: Type.Number(),
  pax_min: Type.Optional(Type.Number()),
  pax_max: Type.Optional(Type.Number()),
  small_luggage_min: Type.Optional(Type.Number()),
  small_luggage_max: Type.Optional(Type.Number()),
  large_luggage_min: Type.Optional(Type.Number()),
  large_luggage_max: Type.Optional(Type.Number()),
  // Objets picture signés (url = clé S3 brute + preview_url/original_url) — même forme que
  // pack : indispensable pour que l'édition re-soumette la clé brute (cf. ImageUploadInput).
  pictures: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  prestation_ids: Type.Array(Type.Number()),
  pro_place_ids: Type.Array(Type.Number()),
  // « Au moins un palier tarifaire actif » — ne concerne que les véhicules : sans palier, le
  // tunnel public de courses exclut le véhicule (BOOKABLE_RIDE_VEHICLE_WHERE), la liste des
  // véhicules s'en sert pour afficher le warning « non réservable en ligne ».
  has_pricing_tiers: Type.Optional(Type.Boolean()),
});
export type ProResourceResponseObject = Static<typeof ProResourceResponseSchema>;

/**
 * Réponse du PUT ressource SEULEMENT : la ressource, plus l'impact réservabilité quand la
 * modification (désactivation, dissociation lieu/prestation — M12) dégrade des plannings.
 * Wrapper dédié pour la même raison que côté prestation : sur le schéma de base, le champ
 * remonterait dans le GET liste et partout où la ressource est imbriquée.
 */
export const ProResourceWithBookabilityImpactResponseSchema = Type.Object({
  ...ProResourceResponseSchema.properties,
  bookability_impact: Type.Optional(AvailabilityBookabilityImpactSchema),
});
export type ProResourceWithBookabilityImpactResponseObject = Static<
  typeof ProResourceWithBookabilityImpactResponseSchema
>;

export const ProResourceListResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  total: Type.Number(),
  items: Type.Array(ProResourceResponseSchema),
});
export type ProResourceListResponseObject = Static<typeof ProResourceListResponseSchema>;

/** Tri de la liste des équipements (`/catalog/equipment`). */
export const PRO_RESOURCE_SORTS: SortConfig = {
  fields: {
    // Seule définition de l'ordre personnalisé des ressources : le backend en dérive
    // PRO_RESOURCE_DISPLAY_ORDER (priorité d'affectation en mode pool, couloirs de l'agenda, réservation).
    manual: {
      label: 'SORT_FIELD_MANUAL',
      value_type: 'fixed',
      default_direction: SortDirection.ASC,
      order_by: (): Record<string, unknown>[] => [
        { sort_order: SortDirection.ASC },
        { category: SortDirection.ASC },
        { name: SortDirection.ASC },
        { pro_resource_id: SortDirection.ASC },
      ],
      api_description:
        "Ordre personnalisé réglé par le pro par glisser-déposer (en mode pool, c'est aussi la priorité d'affectation automatique). Sens unique (sort_dir ignoré). C'est l'ordre par défaut.",
    },
    name: {
      label: 'SORT_FIELD_NAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ name: direction }],
      api_description: 'Nom de la ressource, par ordre alphabétique.',
    },
    quantity: {
      label: 'SORT_FIELD_QUANTITY',
      value_type: 'number',
      default_direction: SortDirection.DESC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ quantity: direction }],
      api_description: "Nombre d'unités disponibles de la ressource.",
    },
  },
  default_field: 'manual',
  tiebreaker: 'pro_resource_id',
};

export const AgendaLaneResourceSchema = Type.Object({
  pro_resource_id: Type.Number(),
  name: Type.String(),
  category: ProResourceCategoryEnum,
  is_exclusive: Type.Boolean(),
  quantity: Type.Number(),
  capacity: Type.Number(),
  has_libre_service_prestation: Type.Boolean(),
});
export type AgendaLaneResourceObject = Static<typeof AgendaLaneResourceSchema>;

export const AgendaLanesResponseSchema = Type.Object({
  items: Type.Array(AgendaLaneResourceSchema),
});
export type AgendaLanesResponseObject = Static<typeof AgendaLanesResponseSchema>;

/**
 * Formulaire Matériel. `capacity` et `is_exclusive` sont OBLIGATOIRES (comme dans
 * `RoomResourceFormSchema`) : laissés vides, ils retombaient sur les défauts Prisma
 * (1 place, partagée), c'est-à-dire la configuration qui ferme les créneaux dès qu'un
 * client réserve à plusieurs. Le pro doit répondre, pas subir un défaut.
 */
export const CreateEquipmentRequestSchema = Type.Object({
  name: NameSchema,
  description: Type.Optional(DescriptionSchema),
  quantity: Type.Optional(QuantitySchema),
  capacity: CapacitySchema,
  is_exclusive: IsExclusiveSchema,
  prestation_ids: Type.Optional(PrestationIdsFormSchema),
  pro_place_ids: Type.Optional(ProPlaceIdsSchema),
});
export type CreateEquipmentRequestObject = Static<typeof CreateEquipmentRequestSchema>;

/**
 * Form schema for Room/poste resources (PLACE_CAPACITY_BLOCK).
 * `quantity` and `pro_place_ids` are hidden — populated via `defaultValues`
 * (quantity = 1, pro_place_ids = [forced_pro_place_id]) and submitted but not rendered.
 */
export const RoomResourceFormSchema = Type.Object({
  name: NameSchema,
  description: Type.Optional(DescriptionSchema),
  capacity: CapacitySchema,
  is_exclusive: IsExclusiveSchema,
  prestation_ids: Type.Optional(PrestationIdsFormSchema),
  // Hidden — injected via defaultValues, not rendered in the form.
  quantity: Type.Optional(Type.Integer({ minimum: 1, meta: { hidden: true } })),
  pro_place_ids: Type.Optional(Type.Array(Type.Integer(), { meta: { hidden: true } })),
});
export type RoomResourceFormObject = Static<typeof RoomResourceFormSchema>;

/**
 * Form schema for Vehicle resources (VEHICLE_PAGE).
 * All vehicle-specific fields are Optional so the form is progressively filled.
 * Une fiche véhicule = une CLASSE de véhicules interchangeables : `quantity` est la taille
 * de la flotte de cette classe (le budget de concurrence du pool est quantity × capacity).
 */
export const VehicleResourceFormSchema = Type.Object({
  name: VehicleNameSchema,
  description: Type.Optional(DescriptionSchema),
  pictures: Type.Optional(PicturesSchema),
  quantity: Type.Optional(VehicleQuantitySchema),
  // Pas d'is_exclusive : une course occupe UNE unité de la flotte, jamais la classe entière —
  // le backend force d'ailleurs is_exclusive = false pour la catégorie VEHICLE.
  pax_min: Type.Optional(PaxMinSchema),
  pax_max: Type.Optional(PaxMaxSchema),
  small_luggage_min: Type.Optional(SmallLuggageMinSchema),
  small_luggage_max: Type.Optional(SmallLuggageMaxSchema),
  large_luggage_min: Type.Optional(LargeLuggageMinSchema),
  large_luggage_max: Type.Optional(LargeLuggageMaxSchema),
  prestation_ids: Type.Optional(PrestationIdsFormSchema),
  pro_place_ids: Type.Optional(ProPlaceIdsFormSchema),
});
export type VehicleResourceFormObject = Static<typeof VehicleResourceFormSchema>;

/**
 * Variante CRÉATION du formulaire véhicule : « Prestations liées » et « Lieux liés »
 * n'apparaissent qu'en modification. À la création, l'auto-association backend lie le
 * véhicule à toutes les prestations « Course » du pro, et l'absence de scoping lieu le
 * rend disponible partout — exposer les sélecteurs ferait double emploi et permettrait
 * de court-circuiter l'auto-lien par une sélection vide.
 */
export const VehicleResourceCreateFormSchema = Type.Composite([
  Type.Omit(VehicleResourceFormSchema, [
    'prestation_ids',
    'pro_place_ids',
    'pax_min',
    'pax_max',
    'small_luggage_min',
    'small_luggage_max',
    'large_luggage_min',
    'large_luggage_max',
  ]),
  Type.Object({
    pax_min: Type.Optional(Type.Integer({ ...PAX_MIN_OPTIONS, default: VEHICLE_DEFAULT_PAX_MIN })),
    pax_max: Type.Optional(Type.Integer({ ...PAX_MAX_OPTIONS, default: VEHICLE_DEFAULT_CAPACITY_MAX })),
    small_luggage_min: Type.Optional(Type.Integer({ ...SmallLuggageMinOptions, default: VEHICLE_DEFAULT_LUGGAGE_MIN })),
    small_luggage_max: Type.Optional(
      Type.Integer({ ...SmallLuggageMaxOptions, default: VEHICLE_DEFAULT_CAPACITY_MAX }),
    ),
    large_luggage_min: Type.Optional(Type.Integer({ ...LargeLuggageMinOptions, default: VEHICLE_DEFAULT_LUGGAGE_MIN })),
    large_luggage_max: Type.Optional(
      Type.Integer({ ...LargeLuggageMaxOptions, default: VEHICLE_DEFAULT_CAPACITY_MAX }),
    ),
  }),
]);
export type VehicleResourceCreateFormObject = Static<typeof VehicleResourceCreateFormSchema>;
